import { useQuery } from '@tanstack/react-query'
import { Button, ChevDownIcon, ChevRightIcon, Pagination, SpinnerWithBackground, Text } from '@viapeople/components'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../components/GenericTable'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { trpc } from '../../../utils/trpc'
import { PecAnonymousHeatmap } from '../components/PecAnonymousHeatmap'
import { PecInteractionGraph } from '../components/PecInteractionGraph'

const ITEMS_PER_PAGE = 25

type SummarySubView = 'averages' | 'individual'

interface OverallStats {
	mode: number | null
	median: number | null
	average: number | null
	min: number | null
	max: number | null
}

interface RatingConfigOption {
	label: string
	value: string | number
}

interface RatingConfig {
	options?: RatingConfigOption[]
}

// Get rating bounds from config or fallback to 1-N
const getRatingBoundsFromConfig = (
	ratingConfig: RatingConfig | null | undefined,
	scale: number,
): { min: number; max: number } => {
	if (ratingConfig?.options?.length) {
		const values = ratingConfig.options.map((o) => Number(o.value)).filter((v) => !Number.isNaN(v))
		if (values.length > 0) {
			return { min: Math.min(...values), max: Math.max(...values) }
		}
	}
	return { min: 1, max: scale }
}

/**
 * @fileoverview PEC Results Tab
 *
 * Display Heat Map with participant averages and statistics
 */

interface HeatMapUser {
	userId: string
	firstName: string
	lastName: string
	email: string
	averageRating: number
	totalResponses: number
	completedResponses: number
	pendingResponses: number
}

// Nickel rounding - round to nearest 0.05
const nickelRound = (value: number): number => {
	return Math.round(value * 20) / 20
}

// Helper function to get color based on rating with support for custom scales
const getRatingColor = (rating: number, maxScale: number = 5, ratingConfig?: RatingConfig | null): string => {
	if (rating === 0) return 'bg-gray-100 text-gray-700'

	const { min, max } = getRatingBoundsFromConfig(ratingConfig, maxScale)
	// Calculate percentage based on position in scale (min to max)
	const percentage = max === min ? 100 : ((rating - min) / (max - min)) * 100

	if (percentage >= 80) return 'bg-green-100 text-green-800'
	if (percentage >= 60) return 'bg-blue-100 text-blue-800'
	if (percentage >= 40) return 'bg-yellow-100 text-yellow-800'
	return 'bg-red-100 text-red-800'
}

// Helper function to format stat with comparison to overall (nickel rounded)
const formatWithComparison = (value: number | null, overallValue: number | null): React.ReactNode => {
	if (value === null || overallValue === null) return 'N/A'
	const roundedValue = nickelRound(value)
	const diff = nickelRound(value - overallValue)
	const colorClass = diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-gray-600'
	const sign = diff > 0 ? '+' : ''
	return (
		<span className={colorClass}>
			{roundedValue.toFixed(2)} ({sign}
			{diff.toFixed(2)})
		</span>
	)
}

// Calculate overall stats from all ratings
const calculateOverallStats = (users: HeatMapUser[]): OverallStats => {
	// We'll use the average ratings from users to calculate overall stats
	const allAverages = users.filter((u) => u.averageRating > 0).map((u) => u.averageRating)

	if (allAverages.length === 0) {
		return { mode: null, median: null, average: null, min: null, max: null }
	}

	const sorted = [...allAverages].sort((a, b) => a - b)
	const median =
		sorted.length % 2 === 0
			? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
			: sorted[Math.floor(sorted.length / 2)]
	const average = allAverages.reduce((sum, val) => sum + val, 0) / allAverages.length

	// Calculate mode
	const frequency: Record<string, number> = {}
	allAverages.forEach((val) => {
		const key = val.toFixed(2)
		frequency[key] = (frequency[key] || 0) + 1
	})
	let mode: number | null = null
	let maxFreq = 0
	for (const [val, freq] of Object.entries(frequency)) {
		if (freq > maxFreq) {
			maxFreq = freq
			mode = Number(val)
		}
	}
	if (maxFreq === 1) mode = null

	return { mode, median, average, min: Math.min(...allAverages), max: Math.max(...allAverages) }
}

type ViewMode = 'heatmap' | 'summary' | 'interactions'

// Expandable User Row Component for Individual Breakdown
interface ExpandableUserRowProps {
	user: HeatMapUser
	pecCycleId: string
	organizationId: string
	overallStats: OverallStats
	maxScale: number
	ratingConfig?: RatingConfig | null
	isExpanded: boolean
	onToggle: () => void
}

const ExpandableUserRow = ({
	user,
	pecCycleId,
	organizationId,
	overallStats,
	maxScale,
	ratingConfig,
	isExpanded,
	onToggle,
}: ExpandableUserRowProps) => {
	const { data: ratingsData, isPending } = useQuery({
		...trpc.client.pec.getUserIndividualRatings.queryOptions({
			pecCycleId,
			userId: user.userId,
			organizationId,
		}),
		enabled: isExpanded,
	})

	return (
		<div className='border border-gray-200 rounded-lg mb-2 overflow-hidden'>
			{/* User Row Header */}
			{/* biome-ignore lint/a11y/useSemanticElements: expandable row header with complex layout */}
			<div
				role='button'
				tabIndex={0}
				className='flex items-center justify-between p-4 bg-white hover:bg-gray-50 cursor-pointer'
				onClick={onToggle}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') onToggle()
				}}>
				<div className='flex items-center gap-4'>
					<div className='text-gray-400'>{isExpanded ? <ChevDownIcon size={20} /> : <ChevRightIcon size={20} />}</div>
					<div>
						<Text variant='small' weight='semibold'>
							{user.firstName} {user.lastName}
						</Text>
						<Text variant='xsmall' className='text-gray-500'>
							{user.email}
						</Text>
					</div>
				</div>
				<div className='flex items-center gap-3'>
					<span
						className={`px-3 py-1 rounded-full text-sm font-semibold ${getRatingColor(user.averageRating, maxScale, ratingConfig)}`}>
						{user.averageRating > 0 ? nickelRound(user.averageRating).toFixed(2) : 'N/A'}
					</span>
					{user.averageRating > 0 && (
						<Text variant='xsmall' className='text-gray-500'>
							/ {getRatingBoundsFromConfig(ratingConfig, maxScale).max}
						</Text>
					)}
				</div>
			</div>
			{/* Expanded Content */}
			{isExpanded && (
				<div className='p-4 bg-gray-50 border-t border-gray-200'>
					{isPending ? (
						<div className='flex justify-center py-4'>
							<SpinnerWithBackground />
						</div>
					) : ratingsData ? (
						<>
							{/* Statistics Row with Comparison to Overall */}
							<div className='grid grid-cols-7 gap-4 mb-4 text-sm'>
								<div>
									<span className='text-gray-500'>Mode:</span>{' '}
									{formatWithComparison(ratingsData.stats.mode, overallStats.mode)}
								</div>
								<div>
									<span className='text-gray-500'>Median:</span>{' '}
									{formatWithComparison(ratingsData.stats.median, overallStats.median)}
								</div>
								<div>
									<span className='text-gray-500'>Average:</span>{' '}
									{formatWithComparison(ratingsData.stats.average, overallStats.average)}
								</div>
								<div>
									<span className='text-gray-500'>Min:</span>{' '}
									{ratingsData.stats.min !== null ? nickelRound(ratingsData.stats.min).toFixed(2) : 'N/A'}
								</div>
								<div>
									<span className='text-gray-500'>Max:</span>{' '}
									{ratingsData.stats.max !== null ? nickelRound(ratingsData.stats.max).toFixed(2) : 'N/A'}
								</div>
								<div>
									<span className='text-gray-500'>Responses:</span>{' '}
									<span className='font-medium'>{ratingsData.stats.count}</span>
								</div>
								<div>
									<span className='text-gray-500'>N/A:</span>{' '}
									<span className='font-medium'>{ratingsData.stats.naCount ?? 0}</span>
								</div>
							</div>

							{/* Rating Tiles - flex-wrap for 50+ tiles */}
							<div className='flex flex-wrap gap-1'>
								{ratingsData.ratings.map((rating, index) => (
									<span
										// biome-ignore lint/suspicious/noArrayIndexKey: ratings are numeric values that can repeat
										key={index}
										className={`px-2 py-1 rounded text-sm font-medium min-w-[32px] text-center ${getRatingColor(rating, maxScale, ratingConfig)}`}>
										{rating}
									</span>
								))}
							</div>

							{ratingsData.ratings.length === 0 && (
								<Text variant='small' className='text-gray-500 italic'>
									No ratings received yet
								</Text>
							)}
						</>
					) : (
						<Text variant='small' className='text-gray-500 italic'>
							Unable to load ratings
						</Text>
					)}
				</div>
			)}
		</div>
	)
}

export const PecResultsTab = () => {
	const { pecId } = useParams<{ pecId: string }>()
	const { user } = sessionStore()
	const organizationId = user?.organizationId

	const [viewMode, setViewMode] = useState<ViewMode>('heatmap')
	const [summarySubView, setSummarySubView] = useState<SummarySubView>('averages')
	const [expandedUserIds, setExpandedUserIds] = useState<Set<string>>(new Set())
	const [searchQuery, setSearchQuery] = useState('')
	const [currentPage, setCurrentPage] = useState(1)
	const [filterByRaterId, setFilterByRaterId] = useState<string | null>(null)
	const [filterByRateeId, setFilterByRateeId] = useState<string | null>(null)

	const { data: heatMapData, isLoading } = useQuery({
		...trpc.client.pec.getHeatMap.queryOptions({
			pecCycleId: pecId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!pecId && !!organizationId,
	})

	// Fetch interaction graph data
	const { data: interactionData, isLoading: isInteractionLoading } = useQuery({
		...trpc.client.pec.getInteractionGraph.queryOptions({
			pecCycleId: pecId as string,
			organizationId: organizationId as string,
			filterByRaterId: filterByRaterId ?? undefined,
			filterByRateeId: filterByRateeId ?? undefined,
		}),
		enabled: !!pecId && !!organizationId && viewMode === 'interactions',
	})

	// Fetch all participant ratings for heatmap table view
	const { data: participantRatingsData, isLoading: isParticipantRatingsLoading } = useQuery({
		...trpc.client.pec.getAllParticipantRatings.queryOptions({
			pecCycleId: pecId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!pecId && !!organizationId && viewMode === 'heatmap',
	})

	// Get rating scale and config from PEC cycle (default to 5)
	const ratingScale = heatMapData?.ratingScale ?? 5
	const ratingConfig = heatMapData?.ratingConfig
	const { max: maxRatingValue } = getRatingBoundsFromConfig(ratingConfig, ratingScale)

	// Filter users based on search query - must be called before any early returns
	const filteredUsers = useMemo(() => {
		if (!heatMapData?.users) return []
		if (!searchQuery.trim()) return heatMapData.users

		const query = searchQuery.toLowerCase().trim()
		return heatMapData.users.filter(
			(user) =>
				user.firstName.toLowerCase().includes(query) ||
				user.lastName.toLowerCase().includes(query) ||
				user.email.toLowerCase().includes(query) ||
				`${user.firstName} ${user.lastName}`.toLowerCase().includes(query),
		)
	}, [heatMapData?.users, searchQuery])

	// Paginate the filtered users
	const paginatedUsers = useMemo(() => {
		const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
		return filteredUsers.slice(startIndex, startIndex + ITEMS_PER_PAGE)
	}, [filteredUsers, currentPage])

	// Reset to page 1 when search changes
	const handleSearchChange = (value: string) => {
		setSearchQuery(value)
		setCurrentPage(1)
	}

	// Expand/collapse all handlers for Individual Breakdown
	const handleExpandAll = () => {
		setExpandedUserIds(new Set(filteredUsers.map((u) => u.userId)))
	}

	const handleCollapseAll = () => {
		setExpandedUserIds(new Set())
	}

	const toggleUserExpanded = (userId: string) => {
		setExpandedUserIds((prev) => {
			const newSet = new Set(prev)
			if (newSet.has(userId)) {
				newSet.delete(userId)
			} else {
				newSet.add(userId)
			}
			return newSet
		})
	}

	// Calculate overall stats for comparison in individual breakdown
	const overallStats = useMemo(() => {
		if (!heatMapData?.users) return { mode: null, median: null, average: null, min: null, max: null }
		return calculateOverallStats(heatMapData.users)
	}, [heatMapData?.users])

	// Calculate counts for key metrics
	const incompleteReviewersCount = heatMapData?.incompleteReviewersCount ?? 0
	const completedReviewersCount = (heatMapData?.totalParticipants ?? 0) - incompleteReviewersCount

	if (isLoading) {
		return <SpinnerWithBackground />
	}

	if (!heatMapData || heatMapData.users.length === 0) {
		return (
			<div className='px-6 mt-6'>
				<div className='bg-gray-50 border border-gray-200 rounded-lg p-8 text-center'>
					<Text variant='medium' weight='semibold' className='text-gray-700'>
						No results yet
					</Text>
					<Text variant='small' className='text-gray-600 mt-2'>
						Results will appear here once the cycle is launched and participants start submitting responses
					</Text>
				</div>
			</div>
		)
	}

	// Summary view columns - simplified view showing just name, email, and average
	const summaryColumns: Column<HeatMapUser>[] = [
		{
			field: 'firstName',
			label: 'First Name',
			colSpan: 2,
			sortable: true,
			render: (user: HeatMapUser) => (
				<Text variant='small' weight='semibold'>
					{user.firstName}
				</Text>
			),
		},
		{
			field: 'lastName',
			label: 'Last Name',
			colSpan: 2,
			sortable: true,
			render: (user: HeatMapUser) => (
				<Text variant='small' weight='semibold'>
					{user.lastName}
				</Text>
			),
		},
		{
			field: 'email',
			label: 'Email',
			colSpan: 4,
			sortable: true,
			render: (user: HeatMapUser) => (
				<Text variant='small' weight='regular' className='text-gray-600'>
					{user.email}
				</Text>
			),
		},
		{
			field: 'averageRating',
			label: 'Average Rating',
			colSpan: 3,
			sortable: true,
			render: (user: HeatMapUser) => {
				const ratingDisplay = user.averageRating > 0 ? nickelRound(user.averageRating).toFixed(2) : 'N/A'
				const colorClass = getRatingColor(user.averageRating, ratingScale, ratingConfig)

				return (
					<div className='flex items-center gap-3'>
						<span className={`px-4 py-2 rounded-full text-sm font-semibold ${colorClass}`}>{ratingDisplay}</span>
						{user.averageRating > 0 && (
							<Text variant='small' className='text-gray-500'>
								out of {maxRatingValue}
							</Text>
						)}
					</div>
				)
			},
		},
	]

	return (
		<div className='px-6 mt-6 max-w-full'>
			{/* Header with View Toggle */}
			<div className='mb-6 flex items-center justify-between'>
				<div>
					<Text variant='large' weight='semibold' className='text-gray-900'>
						{viewMode === 'heatmap'
							? 'Heat Map Results'
							: viewMode === 'summary'
								? 'Summary Results'
								: 'Interaction Graph'}
					</Text>
					<Text variant='small' className='text-gray-600 mt-1'>
						{viewMode === 'heatmap'
							? 'View detailed ratings and response statistics for all participants'
							: viewMode === 'summary'
								? 'View simplified average ratings for all participants'
								: 'View interaction matrix and N/A responses'}
					</Text>
				</div>
				<div className='flex gap-2'>
					<Button variant={viewMode === 'heatmap' ? 'primary' : 'secondary'} onClick={() => setViewMode('heatmap')}>
						Heat Map
					</Button>
					<Button variant={viewMode === 'summary' ? 'primary' : 'secondary'} onClick={() => setViewMode('summary')}>
						Summary
					</Button>
					<Button
						variant={viewMode === 'interactions' ? 'primary' : 'secondary'}
						onClick={() => setViewMode('interactions')}>
						Interactions
					</Button>
				</div>
			</div>

			{/* Summary Statistics */}
			<div className='grid grid-cols-1 md:grid-cols-4 gap-6 mb-8'>
				<div className='bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6 shadow-sm'>
					<Text variant='small' className='text-primary-700 font-medium'>
						Cycle Average
					</Text>
					<div className='flex items-baseline gap-2 mt-2'>
						<Text variant='h4' weight='semibold' className='text-primary-900'>
							{nickelRound(heatMapData.cycleAverage).toFixed(2)}
						</Text>
						<Text variant='small' className='text-primary-600'>
							/ {maxRatingValue}
						</Text>
					</div>
				</div>

				<div className='bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow'>
					<Text variant='small' className='text-gray-600 font-medium'>
						Participants
					</Text>
					<Text variant='h4' weight='semibold' className='text-gray-900 mt-2'>
						{heatMapData.totalParticipants}
					</Text>
				</div>

				<div className='bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow'>
					<Text variant='small' className='text-gray-600 font-medium'>
						Haven't Responded
					</Text>
					<Text variant='h4' weight='semibold' className='text-gray-900 mt-2'>
						{incompleteReviewersCount}
					</Text>
				</div>

				<div className='bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow'>
					<Text variant='small' className='text-gray-600 font-medium'>
						Completion Rate
					</Text>
					<div className='flex items-center gap-3 mt-2'>
						<Text variant='h4' weight='semibold' className='text-gray-900'>
							{heatMapData.totalParticipants > 0
								? ((completedReviewersCount / heatMapData.totalParticipants) * 100).toFixed(1)
								: 0}
							%
						</Text>
						<div className='flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]'>
							<div
								className='bg-primary-600 h-2 rounded-full transition-all duration-300'
								style={{
									width: `${heatMapData.totalParticipants > 0 ? (completedReviewersCount / heatMapData.totalParticipants) * 100 : 0}%`,
								}}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Rating Scale Legend - only show in heat map view */}
			{viewMode === 'heatmap' && (
				<div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
					<Text variant='small' weight='semibold' className='text-blue-900 mb-2'>
						Rating Scale Legend
					</Text>
					<div className='flex flex-wrap gap-4'>
						<div className='flex items-center gap-2'>
							<span className='px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800'>
								Excellent
							</span>
							<Text variant='xsmall' className='text-blue-700'>
								80%+ of max scale
							</Text>
						</div>
						<div className='flex items-center gap-2'>
							<span className='px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800'>Good</span>
							<Text variant='xsmall' className='text-blue-700'>
								60-79% of max scale
							</Text>
						</div>
						<div className='flex items-center gap-2'>
							<span className='px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800'>
								Average
							</span>
							<Text variant='xsmall' className='text-blue-700'>
								40-59% of max scale
							</Text>
						</div>
						<div className='flex items-center gap-2'>
							<span className='px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800'>
								Needs Improvement
							</span>
							<Text variant='xsmall' className='text-blue-700'>
								Below 40% of max scale
							</Text>
						</div>
					</div>
				</div>
			)}

			{/* Summary View Key Metrics - only show in summary mode */}
			{viewMode === 'summary' && (
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
					<div className='bg-white border border-gray-200 rounded-lg p-6 shadow-sm'>
						<Text variant='small' className='text-gray-600 font-medium'>
							Completed
						</Text>
						<Text variant='h4' weight='semibold' className='text-green-600 mt-2'>
							{completedReviewersCount}
						</Text>
						<Text variant='xsmall' className='text-gray-500'>
							{heatMapData.totalParticipants > 0
								? ((completedReviewersCount / heatMapData.totalParticipants) * 100).toFixed(0)
								: 0}
							%
						</Text>
					</div>

					<div className='bg-white border border-gray-200 rounded-lg p-6 shadow-sm'>
						<Text variant='small' className='text-gray-600 font-medium'>
							Haven't Responded
						</Text>
						<Text variant='h4' weight='semibold' className='text-red-600 mt-2'>
							{incompleteReviewersCount}
						</Text>
						<Text variant='xsmall' className='text-gray-500'>
							{heatMapData.totalParticipants > 0
								? ((incompleteReviewersCount / heatMapData.totalParticipants) * 100).toFixed(0)
								: 0}
							%
						</Text>
					</div>
				</div>
			)}

			{/* Summary Sub-tabs - only show in summary mode */}
			{viewMode === 'summary' && (
				<div className='flex gap-2 mb-6'>
					<Button
						variant={summarySubView === 'averages' ? 'primary' : 'secondary'}
						onClick={() => setSummarySubView('averages')}
						size='small'>
						Averages
					</Button>
					<Button
						variant={summarySubView === 'individual' ? 'primary' : 'secondary'}
						onClick={() => setSummarySubView('individual')}
						size='small'>
						Individual Breakdown
					</Button>
				</div>
			)}

			{/* Anonymous Heatmap */}
			{viewMode === 'heatmap' &&
				(isParticipantRatingsLoading ? (
					<SpinnerWithBackground />
				) : participantRatingsData && heatMapData ? (
					<PecAnonymousHeatmap
						ratingDistribution={heatMapData.ratingDistribution}
						ratingScale={participantRatingsData.ratingScale}
						ratingConfig={participantRatingsData.ratingConfig}
						cycleAverage={heatMapData.cycleAverage}
						overallStats={participantRatingsData.overallStats}
						participants={participantRatingsData.participants}
					/>
				) : (
					<div className='text-center py-8'>
						<Text variant='small' className='text-gray-500'>
							No heatmap data available
						</Text>
					</div>
				))}

			{/* Summary Averages Table */}
			{viewMode === 'summary' && summarySubView === 'averages' && (
				<div className='bg-white border border-gray-200 rounded-lg p-6 shadow-sm'>
					<div className='flex items-center justify-between mb-6'>
						<div>
							<Text variant='medium' weight='semibold' className='text-gray-900'>
								Average Ratings Summary
							</Text>
							<Text variant='small' className='text-gray-600 mt-1'>
								{heatMapData.users.length} participants in this cycle
							</Text>
						</div>
					</div>

					<GenericTable<HeatMapUser, Record<string, never>>
						data={paginatedUsers}
						columns={summaryColumns}
						queryOptionsGenerator={() => ({ enabled: false })}
						searchValue={searchQuery}
						onSearchChange={handleSearchChange}
						externalDataFetching={true}
						showPagination={false}
						idField='userId'
						emptyState={{
							title: searchQuery ? 'No matching participants' : 'No participant data',
							description: searchQuery
								? 'Try adjusting your search terms'
								: 'Participant results will appear here once responses are submitted',
						}}
					/>

					{filteredUsers.length > ITEMS_PER_PAGE && (
						<Pagination
							currentPage={currentPage}
							setCurrentPage={setCurrentPage}
							itemsPerPage={ITEMS_PER_PAGE}
							totalItems={filteredUsers.length}
						/>
					)}
				</div>
			)}

			{/* Summary Individual Breakdown */}
			{viewMode === 'summary' && summarySubView === 'individual' && (
				<div className='bg-white border border-gray-200 rounded-lg p-6 shadow-sm'>
					<div className='flex items-center justify-between mb-6'>
						<div>
							<Text variant='medium' weight='semibold' className='text-gray-900'>
								Individual Breakdown
							</Text>
							<Text variant='small' className='text-gray-600 mt-1'>
								Click on a participant to view their individual ratings
							</Text>
						</div>
						<div className='flex gap-2'>
							<Button variant='secondary' size='small' onClick={handleExpandAll}>
								Expand All
							</Button>
							<Button variant='secondary' size='small' onClick={handleCollapseAll}>
								Collapse All
							</Button>
						</div>
					</div>

					{/* Overall Statistics Banner */}
					<div className='p-4 bg-gray-100 rounded-lg mb-4'>
						<Text variant='small' weight='semibold' className='text-gray-700 mb-2'>
							Overall Cycle Statistics
						</Text>
						<div className='grid grid-cols-5 gap-4 text-sm'>
							<div>
								<span className='text-gray-500'>Mode:</span>{' '}
								<span className='font-medium'>
									{overallStats.mode !== null ? nickelRound(overallStats.mode).toFixed(2) : 'N/A'}
								</span>
							</div>
							<div>
								<span className='text-gray-500'>Median:</span>{' '}
								<span className='font-medium'>
									{overallStats.median !== null ? nickelRound(overallStats.median).toFixed(2) : 'N/A'}
								</span>
							</div>
							<div>
								<span className='text-gray-500'>Average:</span>{' '}
								<span className='font-medium'>
									{overallStats.average !== null ? nickelRound(overallStats.average).toFixed(2) : 'N/A'}
								</span>
							</div>
							<div>
								<span className='text-gray-500'>Min:</span>{' '}
								<span className='font-medium'>
									{overallStats.min !== null ? nickelRound(overallStats.min).toFixed(2) : 'N/A'}
								</span>
							</div>
							<div>
								<span className='text-gray-500'>Max:</span>{' '}
								<span className='font-medium'>
									{overallStats.max !== null ? nickelRound(overallStats.max).toFixed(2) : 'N/A'}
								</span>
							</div>
						</div>
					</div>

					{/* Expandable User Rows */}
					<div className='space-y-2'>
						{filteredUsers.map((user) => (
							<ExpandableUserRow
								key={user.userId}
								user={user}
								pecCycleId={pecId as string}
								organizationId={organizationId as string}
								overallStats={overallStats}
								maxScale={ratingScale}
								ratingConfig={ratingConfig}
								isExpanded={expandedUserIds.has(user.userId)}
								onToggle={() => toggleUserExpanded(user.userId)}
							/>
						))}
					</div>

					{filteredUsers.length === 0 && (
						<div className='text-center py-8'>
							<Text variant='small' className='text-gray-500'>
								{searchQuery ? 'No matching participants' : 'No participant data'}
							</Text>
						</div>
					)}
				</div>
			)}

			{/* Interaction Graph View */}
			{viewMode === 'interactions' && (
				<div className='bg-white border border-gray-200 rounded-lg p-6 shadow-sm'>
					{isInteractionLoading ? (
						<SpinnerWithBackground />
					) : interactionData ? (
						<PecInteractionGraph
							participants={interactionData.participants}
							interactions={interactionData.interactions}
							naResponses={interactionData.naResponses}
							filterByRaterId={filterByRaterId}
							filterByRateeId={filterByRateeId}
							onFilterByRaterChange={setFilterByRaterId}
							onFilterByRateeChange={setFilterByRateeId}
						/>
					) : (
						<div className='text-center py-8'>
							<Text variant='small' className='text-gray-500'>
								No interaction data available
							</Text>
						</div>
					)}
				</div>
			)}
		</div>
	)
}
